import type { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { methodsForSecurityLevel } from '@dealstack/shared/lib/common/eideasy/methodsForSecurityLevel';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { PublicSignerType } from '@dealstack/shared/lib/publicSignerType';
import { webIsProduction } from '@dealstack/frontend/shared/environment';
import type { PublicSigningInfo } from './usePublicSigningInfo';

/**
 * Reads the programme portal token that `ProgrammeSigningPage` attaches as
 * router state when handing a per-row Sign off to the single-document flow.
 * Router state is untyped and survives reloads via the history entry, so it is
 * narrowed structurally rather than trusted. Returns null for visitors who
 * arrived any other way (e.g. straight from an email link) — they have no
 * portal to go back to.
 */
export const getProgrammeReturnToken = (state: unknown): string | null => {
	if (
		typeof state === 'object' &&
		state !== null &&
		'programmeToken' in state &&
		typeof state.programmeToken === 'string'
	) {
		return state.programmeToken;
	}
	return null;
};

/** The authorised-signatory portal URL for a programme-scoped public signing token. */
export const buildProgrammePortalPath = (programmeToken: string) =>
	`/public-signing?token=${encodeURIComponent(programmeToken)}`;

const PUBLIC_SIGNER_ROLE_TYPE: Record<PublicSignerType, DocumentSigningRoleType> = {
	[PublicSignerType.Witness]: DocumentSigningRoleType.WITNESS,
	[PublicSignerType.Counterparty]: DocumentSigningRoleType.COUNTERPARTY,
	[PublicSignerType.Representative]: DocumentSigningRoleType.REPRESENTATIVE,
	[PublicSignerType.Certifier]: DocumentSigningRoleType.CERTIFIER,
};

/**
 * The signing methods offered to a public signer, derived from their effective
 * security level and role. Empty until the signing info has loaded. Simple sign
 * is only enabled outside production (DC-920).
 */
export const resolveAllowedMethods = (
	signingInfo: PublicSigningInfo | undefined
): EidEasySigningMethod[] => {
	if (!signingInfo) {
		return [];
	}
	return methodsForSecurityLevel(
		signingInfo.effectiveSecurityLevel,
		PUBLIC_SIGNER_ROLE_TYPE[signingInfo.signerType],
		{ enableSimpleSign: !webIsProduction }
	);
};
